import React, { useState, useRef, useEffect, useLayoutEffect } from "react";
import { createPortal } from "react-dom";
import styles from "../../_css/ChatPopup.module.css";
import { X } from "lucide-react";
import SessionActions from "./SessionActions";
import {
  chatMessage,
  endSession,
  startChat,
  updateSessionDuration,
} from "../../requests";
import { deleteAuthToken, notifyError } from "../../utils/helper";
import ResponseLoader from "../common/ResponseLoader";
import DeleteUserModal from "../Modals/DeleteUserModal";
import { useSelector } from "react-redux";
import { CHAT_POPUP, DOWNLOAD_SUMMARY_MODAL } from "../../data/modelsData";
import {
  CustomButton,
  CustomButtonOutline,
  CustomButtonOutlineLight,
} from "../UI/CustomButtons";
import {
  formatHTMLResponse,
  isHTML,
  isUserChatSender,
  toLiveChatHistory,
} from "../helper/formatters";
import ERROR_MSGS from "../../data/errorMsgs";
import useSessionTimer from "../hooks/useSessionTimer";


const DR_AVATAR = `${process.env.PUBLIC_URL}/img/dr-salah-icon.png`;

const IMG_BASE = `${process.env.PUBLIC_URL}/img`;
const SEND_ICON = `${IMG_BASE}/web-ask-salah-icone-enter.png`;
const PAUSE_ICON = `${IMG_BASE}/web-ask-salah-icone-pause.png`;
const PAUSE_ICON_HOVER = `${IMG_BASE}/web-ask-salah-icone-pause-blue.png`;
const PLAY_ICON = `${IMG_BASE}/web-ask-salah-icone-play-white.png`;
const PLAY_ICON_HOVER = `${IMG_BASE}/web-ask-salah-icone-play-blue.png`;
const END_ICON = `${IMG_BASE}/web-ask-salah-icone-call.png`;
const END_ICON_HOVER = `${IMG_BASE}/web-ask-salah-icone-end-red.png`;

const DEFAULT_TIMER_MINUTES = 45;

const summaryMessages = [
  {
    sender: "ai",
    text: "A calming, guided session designed to help users release physical pain and emotional tension through focused breathing, body awareness, and mindful relaxation techniques. This exercise promotes inner balance and supports overall well-being.A calming, guided session designed to help users release physical pain and emotional tension through focused breathing, body awareness, and mindful relaxation techniques. This exercise promotes inner balance and supports overall well-being.A calming, guided session designed to help users release physical pain and emotional tension through focused breathing, body awareness, and mindful relaxation techniques. This exercise promotes inner balance and supports overall well-being.A calming, guided session designed to help users release physical pain and emotional tension through focused breathing, body awareness, and mindful relaxation techniques. This exercise promotes inner balance and supports overall well-being.",
    summary: true,
  },
];

const ChatPopup = ({
  show,
  onClose,
  setShowDownloadSummary,
  header,
  refreshReviews,
  onDownload,
  pdfStatus,
}) => {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [waiting, setWaiting] = useState(false);
  const [hovered, setHovered] = useState("");
  const [showSaveSession, setShowSaveSession] = useState(false);
  const [summary, setSummary] = useState(false);
  const [userSessionId, setUserSessdionId] = useState(null);
  const [showDeleteUserModal, setShowDeleteUserModal] = useState(false);
  const [sessionHistory, setSessionHistory] = useState(false);
  const [sessionStatus, setSessionStatus] = useState(null);
  const [isKeyboardOpen, setIsKeyboardOpen] = useState(false);
  const [isSessionPaused, setIsSessionPaused] = useState(false);
  const [initialSpentSeconds, setInitialSpentSeconds] = useState(0);


  // added for freshstart and resume session and completed session
  const TOTAL_SECONDS = DEFAULT_TIMER_MINUTES * 60;
  const isAlreadyExpired = initialSpentSeconds >= TOTAL_SECONDS;

  const { timerText: timer, spentSeconds, setPaused, reset: resetTimer } =
    useSessionTimer({
      show,
      userSessionId,
      totalSeconds: DEFAULT_TIMER_MINUTES * 60,
      initialSpentSeconds,
      isPaused: isSessionPaused,
      onExpire: () => {
        if (isAlreadyExpired) {
          return;
        }
        onClose?.();
        handleEndSession();
      },
      onDurationChange: (duration) => {
        if (!userSessionId) return;
        updateSessionDuration({
          userSessionId,
          duration,
        });
      },
    });

  const lang = useSelector((state) => state.language.lang);
  const {
    summaryText,
    inputPlaceholder,
    endSessionText,
    sureText,
    endSessionBtnText,
    cancelText,
    continueSessionText,
    sessionCantBeContinuedText,
  } = CHAT_POPUP[lang];
  const { btnText } = DOWNLOAD_SUMMARY_MODAL[lang];

  const chatEndRef = useRef(null);
  const chatBodyRef = useRef(null);
  const overlayRef = useRef(null);
  const popupRef = useRef(null);
  const inputRef = useRef(null);
  const stickToBottomRef = useRef(true);

  const isMobileViewport = () =>
    typeof window !== "undefined" &&
    (window.innerWidth <= 434 || "ontouchstart" in window);

  const scrollChatToBottom = (force = false) => {
    const el = chatBodyRef.current;
    if (!el) return;
    if (!force && !stickToBottomRef.current) return;
    // Assign scrollTop on the chat body only. scrollIntoView() also scrolls
    // ancestor containers (overlay/page) and on Android jumps back to the
    // first message after a reply appears.
    el.scrollTop = el.scrollHeight;
  };

  const focusComposer = () => {
    const input = inputRef.current;
    const chatBody = chatBodyRef.current;
    if (!input || waiting || isSessionPaused) return;

    const previousScrollTop = chatBody?.scrollTop ?? 0;
    input.focus({ preventScroll: true });
    if (!chatBody) return;

    // Android can still shift ancestor scroll on focus even with preventScroll.
    // Restore the transcript, then pin to the latest message.
    chatBody.scrollTop = stickToBottomRef.current
      ? chatBody.scrollHeight
      : previousScrollTop;
    requestAnimationFrame(() => scrollChatToBottom(true));
  };

  const sessionData = sessionStorage.getItem("sessionData");
  const sessionDataParsed = JSON.parse(sessionData);
  const sessionId = sessionDataParsed?.id;

  const userInfo = JSON.parse(sessionStorage.getItem("sal_userInfo"));
  const userName = userInfo?.fullName || "User";
  const AI_AVATAR = userInfo?.profileImage || `${process.env.PUBLIC_URL}/img/profile-image-default.jpg`;

  // Pause/resume is handled inside useSessionTimer (single source of truth)

  useEffect(() => {
    // resume session block
    let sessionResume = false;
    const resumeSessionData = sessionStorage.getItem("resumeSessionData");
    const chatHistory = sessionStorage.getItem("chatHistory");

    if (resumeSessionData && chatHistory) {
      sessionResume = true;
      const resumeSessionDataParsed = JSON.parse(resumeSessionData);
      setSessionHistory(true);
      setSessionStatus(resumeSessionDataParsed?.status);

      // set user session id
      const userSessionId = resumeSessionDataParsed?.userSessionId;
      setUserSessdionId(userSessionId);


      // initialize timer from backend duration (spent seconds)
      const duration = Number(resumeSessionDataParsed?.duration || 0);
      setInitialSpentSeconds(Number.isFinite(duration) ? duration : 0);

      // set chat history (same sender tokens as a live text session)
      const chatHistoryParsed = toLiveChatHistory(
        JSON.parse(chatHistory),
        userName,
        "ai"
      );
      setMessages(chatHistoryParsed);
    } else if (show) {
      setSessionHistory(false);
      setInitialSpentSeconds(0);
    }

    if (show) {
      if (!sessionResume) {
        handleStartChat();
      }
    } else {
      resetStates(false);
    }
    return () => {
      resetStates(false);
    };
  }, [show]);

  useLayoutEffect(() => {
    stickToBottomRef.current = true;
    scrollChatToBottom(true);
    const frame = requestAnimationFrame(() => scrollChatToBottom(true));
    // HTML replies can finish wrapping after the first paint; a short follow-up
    // keeps the latest AI message fully in view without ancestor scrolling.
    const retry = setTimeout(() => scrollChatToBottom(true), 150);
    return () => {
      cancelAnimationFrame(frame);
      clearTimeout(retry);
    };
  }, [messages, waiting]);

  useEffect(() => {
    const focusTimer = setTimeout(() => {
      stickToBottomRef.current = true;
      focusComposer();
      // Keyboard animation on Android finishes after focus; keep the latest
      // reply in view once the visual viewport settles.
      setTimeout(() => scrollChatToBottom(true), 250);
    }, 100);
    return () => clearTimeout(focusTimer);
  }, [messages, waiting, isSessionPaused]);

  // Auto-resize textarea
  useEffect(() => {
    const textarea = inputRef.current;
    if (textarea) {
      const minHeight = 48;
      // Reset height to auto to get the correct scrollHeight
      textarea.style.height = "auto";
      // Set height based on scrollHeight, but keep at least control height and cap at max (10 rows)
      const maxHeight = parseFloat(getComputedStyle(textarea).lineHeight) * 10;
      textarea.style.height = `${Math.max(
        minHeight,
        Math.min(textarea.scrollHeight, maxHeight)
      )}px`;
    }
  }, [input]);

  // Pin the popup to the visual viewport on mobile so the keyboard cannot
  // push the latest reply off-screen or scroll the session back to the top.
  useEffect(() => {
    if (!show) return;

    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    const syncViewport = () => {
      const overlay = overlayRef.current;
      const popup = popupRef.current;
      const vv = window.visualViewport;
      const isMobile = window.innerWidth <= 434;

      if (!overlay || !popup) return;

      if (!isMobile) {
        overlay.style.top = "";
        overlay.style.left = "";
        overlay.style.height = "";
        overlay.style.width = "";
        popup.style.height = "";
        popup.style.maxHeight = "";
        setIsKeyboardOpen(false);
        return;
      }

      const height = vv?.height ?? window.innerHeight;
      const width = vv?.width ?? window.innerWidth;
      const offsetTop = vv?.offsetTop ?? 0;
      const offsetLeft = vv?.offsetLeft ?? 0;

      overlay.style.top = `${offsetTop}px`;
      overlay.style.left = `${offsetLeft}px`;
      overlay.style.height = `${height}px`;
      overlay.style.width = `${width}px`;
      popup.style.height = `${height}px`;
      popup.style.maxHeight = `${height}px`;

      const keyboardOpen = vv ? window.innerHeight - vv.height > 120 : false;
      setIsKeyboardOpen(keyboardOpen);

      if (stickToBottomRef.current) {
        scrollChatToBottom();
      }
    };

    syncViewport();
    window.visualViewport?.addEventListener("resize", syncViewport);
    window.visualViewport?.addEventListener("scroll", syncViewport);
    window.addEventListener("resize", syncViewport);

    return () => {
      document.body.style.overflow = prevOverflow;
      window.visualViewport?.removeEventListener("resize", syncViewport);
      window.visualViewport?.removeEventListener("scroll", syncViewport);
      window.removeEventListener("resize", syncViewport);
    };
  }, [show]);

  const resetStates = (clearTimer = false) => {
    // Best-effort final duration sync before UI teardown
    if (userSessionId && spentSeconds > 0) {
      updateSessionDuration({
        userSessionId,
        duration: spentSeconds,
      });
    }

    setMessages([]);
    setInput("");
    setWaiting(false);
    setHovered("");
    setShowSaveSession(false);
    setSessionHistory(false);
    setSessionStatus(null);
    setIsKeyboardOpen(false);
    setIsSessionPaused(false);
    setUserSessdionId(null);
    setInitialSpentSeconds(0);
    if (clearTimer) {
      resetTimer();
    }
  };

  const handleEndSession = async () => {
    try {
      const response = await endSession({
        userSessionId,
      });
      if (response.status === 200) {
        refreshReviews();
      }
    } catch (error) {
      if (error?.response?.status === 401) {
        deleteAuthToken();
        window.location.reload();
      }
      console.error("Error Initiating Chat:", error);
    }
    // session ended -> clear persisted timer state
    resetStates(true);
    setShowDownloadSummary(true);
    setShowDeleteUserModal(false);
  };

  const handleStartChat = async () => {
    setWaiting(true);
    try {
      const response = await startChat({
        sessionId,
        lang,
      });
      // Trigger a refresh of the current page to get updated data

      let aiText = response?.data?.message;
      if (!aiText) {
        aiText = ERROR_MSGS[lang].chatStartError;
      }
      let userSessionId = response?.data?.userSessionId;
      if (!userSessionId) {
        userSessionId = undefined;
        sessionStorage.removeItem("userSessionId");
        setUserSessdionId(null);
      } else {
        sessionStorage.setItem("userSessionId", userSessionId);
      }
      setInput("");
      setUserSessdionId(userSessionId);
      setMessages([{ sender: "ai", text: aiText }]);
    } catch (error) {
      if (error?.response?.status === 401) {
        deleteAuthToken();
        window.location.reload();
      } else if (error?.response?.status === 400) {
        notifyError(error?.response?.data?.message[lang]);
        onClose?.();
      }
      sessionStorage.removeItem("userSessionId");
      setUserSessdionId(null);
      let aiText =
        ERROR_MSGS[lang].chatStartError;
      setInput("");
      setMessages([{ sender: "ai", text: aiText }]);
      console.error("Error Initiating Chat:", error);
    }
    setWaiting(false);
  };

  const handleChat = async (userMessage) => {
    if (!messages.length) {
      return;
    } else if (!userSessionId) {
      return ERROR_MSGS[lang].chatStartError;
    }
    try {
      const response = await chatMessage({
        userSessionId,
        user_msg: userMessage,
      });
      const aiText = response.data.message;

      return aiText;
    } catch (error) {
      if (error?.response?.status === 401) {
        deleteAuthToken();
        window.location.reload();
      }
      console.error("Error Sending Message:", error);
    }
  };

  const handleSend = async () => {
    if (!input.trim()) return;
    stickToBottomRef.current = true;
    // Auto-resume session if paused when user sends a message
    if (isSessionPaused) {
      setIsSessionPaused(false);
    }

    // to regenerate summary
    if (summary) {
      setSummary(false);
    }
    setMessages((msgs) => [...msgs, { sender: userName, text: input }]);
    setInput("");
    setWaiting(true);
    const aiText = await handleChat(input);
    setMessages((msgs) => [...msgs, { sender: "ai", text: aiText }]);
    setWaiting(false);
  };

  const handleGenerateSummary = () => {
    setInput("");
    setWaiting(true);
    setTimeout(() => {
      setMessages((msgs) => [...msgs, ...summaryMessages]);
      setWaiting(false);
      setTimeout(() => {
        stickToBottomRef.current = true;
        scrollChatToBottom();
      }, 100);
    }, 1500 + Math.random() * 1000);
    setSummary(true);
    setShowSaveSession(false);
  };

  const handleResumeSession = () => {
    setSessionHistory(false);
  };

  const handlePauseResumeSession = () => {
    setIsSessionPaused(!isSessionPaused);
  };

  return (
    <>
      {show && (
        <div className={styles.overlay} ref={overlayRef}>
          <div
            ref={popupRef}
            className={`${styles.popup} ${
              isKeyboardOpen ? styles.keyboardOpen : ""
            }`}
          >
            <div className={styles.header}>
              <h4 className={styles.title}>{header}</h4>
              <button className={styles.closeBtn} onClick={onClose}>
                <X size={22} />
              </button>
            </div>
            <div
              className={styles.chatBody}
              ref={chatBodyRef}
              onScroll={() => {
                const el = chatBodyRef.current;
                if (!el) return;
                const distanceFromBottom =
                  el.scrollHeight - el.scrollTop - el.clientHeight;
                stickToBottomRef.current = distanceFromBottom < 120;
              }}
            >
              {messages.map((msg, idx) => {
                const isUserMsg = isUserChatSender(msg.sender);
                return (
                <div
                  key={idx}
                  className={
                    isUserMsg ? styles.drMsg : styles.aiMsg
                  }
                >
                  <div className={styles.msgText}>
                    <div className={styles.avatar}>
                      <i>
                        <img
                          src={isUserMsg ? AI_AVATAR : DR_AVATAR}
                          alt="avatar"
                        />
                      </i>

                      <span>{isUserMsg ? userName : ""}</span>
                    </div>
                    {msg?.summary &&
                      (isHTML(summaryText) ? (
                        <strong
                          dangerouslySetInnerHTML={{
                            __html: formatHTMLResponse(summaryText),
                          }}
                        >
                          {summaryText}
                        </strong>
                      ) : (
                        <strong>{summaryText}</strong>
                      ))}
                    {isHTML(msg.text) ? (
                      <div
                        dangerouslySetInnerHTML={{
                          __html: formatHTMLResponse(msg.text),
                        }}
                      />
                    ) : (
                      <p className="m-0 mt-2">{formatHTMLResponse(msg.text)}</p>
                    )}
                  </div>
                </div>
                );
              })}
              {waiting && (
                <div className={styles.aiMsg}>
                  <div className={styles.msgText}>
                    <div className={styles.avatar}>
                      <i>
                        <img src={DR_AVATAR} alt="avatar" />
                      </i>
                    </div>

                    <ResponseLoader />
                  </div>
                </div>
              )}
              <div ref={chatEndRef} />
            </div>
            <hr className="m-0" />
            {!sessionHistory ? (
              <div className={styles.inputRow}>
                <textarea
                  className={styles.input}
                  value={input}
                  ref={inputRef}
                  onChange={(e) => setInput(e.target.value)}
                  onKeyDown={(e) => {
                    // On mobile, Enter should always create a new line
                    // On desktop, Enter submits (Shift+Enter creates new line)
                    const isMobile = window.innerWidth <= 434 || 'ontouchstart' in window;
                    
                    if (e.key === "Enter" && !e.shiftKey && !waiting && !isSessionPaused) {
                      // On mobile, allow Enter to create new line (don't prevent default)
                      if (!isMobile) {
                        // On desktop, Enter submits the message
                        e.preventDefault();
                        handleSend();
                      }
                      // On mobile, let Enter create a new line (default behavior)
                    }
                  }}
                  onFocus={() => {
                    stickToBottomRef.current = true;
                    scrollChatToBottom(true);
                    requestAnimationFrame(() => scrollChatToBottom(true));
                    setTimeout(() => scrollChatToBottom(true), 200);
                  }}
                  onTouchStart={(e) => {
                    if (isMobileViewport() && !waiting && !isSessionPaused) {
                      e.target.focus({ preventScroll: true });
                    }
                  }}
                  placeholder={
                    isSessionPaused
                      ? "Session is paused - click resume to continue"
                      : inputPlaceholder
                  }
                  disabled={waiting || isSessionPaused}
                  rows={1}
                  autoComplete="off"
                  autoCorrect="on"
                  autoCapitalize="sentences"
                  spellCheck="true"
                  inputMode="text"
                />
                <div className={styles.bottomBar}>
                  <span className={styles.bottomIconTime}>{timer}</span>

                  <div
                    onClick={() => {
                      if (waiting || !input.trim() || isSessionPaused) return;
                      handleSend();
                    }}
                    className={[
                      styles.bottomIconWrap,
                      input.trim()
                        ? styles.sendIconActive
                        : styles.sendIconInactive,
                      waiting || !input.trim() || isSessionPaused
                        ? styles.disabled
                        : "",
                    ]
                      .filter(Boolean)
                      .join(" ")}
                  >
                    <img
                      src={SEND_ICON}
                      alt="Send"
                      className={styles.sendIconImg}
                    />
                  </div>

                  <div
                    className={styles.bottomIconWrap}
                    onMouseEnter={() => setHovered("pauseResume")}
                    onMouseLeave={() => setHovered("")}
                    onClick={handlePauseResumeSession}
                  >
                    <img
                      src={
                        isSessionPaused
                          ? hovered === "pauseResume"
                            ? PLAY_ICON_HOVER
                            : PLAY_ICON
                          : hovered === "pauseResume"
                          ? PAUSE_ICON_HOVER
                          : PAUSE_ICON
                      }
                      alt={isSessionPaused ? "Resume" : "Pause"}
                      className={styles.bottomIconImg}
                    />
                    <span
                      className={
                        styles.bottomTextAnimated + " " + hovered ===
                        "pauseResume"
                          ? styles.bottomTextShow
                          : styles.bottomTextHide
                      }
                    >
                      {isSessionPaused ? "Resume Session" : "Pause Session"}
                    </span>
                  </div>

                  <div
                    className={styles.bottomIconWrapDanger}
                    onMouseEnter={() => setHovered("endSession")}
                    onMouseLeave={() => setHovered("")}
                    onClick={() => setShowDeleteUserModal(true)}
                  >
                    <img
                      src={hovered === "endSession" ? END_ICON_HOVER : END_ICON}
                      alt="End session"
                      className={styles.bottomIconImg}
                    />
                    <span
                      className={
                        styles.bottomTextAnimated + " " + hovered ===
                        "endSession"
                          ? styles.bottomTextShow
                          : styles.bottomTextHide
                      }
                    >
                      {endSessionText}
                    </span>
                  </div>
                </div>
              </div>
            ) : (
              <div
                className={`${styles.inputRowSummary} d-flex justify-content-between`}
              >
                <span>
                  {sessionStatus === 0
                    ? continueSessionText
                    : sessionCantBeContinuedText}
                </span>
                <div className={`d-flex gap-2 ${styles.buttonRowSummary}`}>
                  <CustomButtonOutline
                    text={lang === "ar" ? "استمرار الجلسة" : "Resume Session"}
                    onClick={handleResumeSession}
                    width="100%"
                    disabled={sessionStatus !== 0}
                  />
                  <CustomButton
                    icon="download"
                    gap="8px"
                    text={pdfStatus || btnText[1]}
                    onClick={onDownload}
                    width="100%"
                    disabled={pdfStatus}
                  />
                </div>
              </div>
            )}
          </div>
        </div>
      )}
      {showSaveSession &&
        createPortal(
          <SessionActions
            show={showSaveSession}
            onHide={() => setShowSaveSession(false)}
            generateSummary={handleGenerateSummary}
            saveSession={() => {}}
            summary={summary}
          />,
          document.body
        )}
      {showDeleteUserModal &&
        createPortal(
          <DeleteUserModal
            show={showDeleteUserModal}
            onHide={() => setShowDeleteUserModal(false)}
            onEndSession={handleEndSession}
            title={sureText}
            btnText={endSessionBtnText}
            cancelText={cancelText}
          />,
          document.body
        )}
    </>
  );
};

export default ChatPopup;
